import React, { forwardRef, useCallback, useState } from "react";

import { useCheckPermission } from "@/domain/_shared/permission/hooks/api/use-check-permission";
import { useCheckPermissionList } from "@/domain/_shared/permission/hooks/api/use-check-permission-list";
import { type PermissionCheck } from "@/domain/_shared/permission/models/permission-types";

export interface PermissionPrefetchProps extends PermissionCheck {}

export interface PrefetchConfig {
  to?: string;
  prefetch?: {
    permission?: PermissionPrefetchProps;
    permissionList?: PermissionPrefetchProps[];
  };
  onMouseEnter?: React.MouseEventHandler<HTMLElement>;
}

export interface PrefetchHandlerProps {
  prefetchConfig?: PrefetchConfig;
  children:
    | ((props: { onMouseEnter: (event: React.MouseEvent<HTMLElement>) => void }) => React.ReactElement)
    | React.ReactElement;
}

/**
 * Custom Link component that provides prefetching capability without rendering any DOM element
 */
export const PrefetchHandler = forwardRef<HTMLElement, PrefetchHandlerProps>(
  ({ children, prefetchConfig = {} }, ref) => {
    const [hasHovered, setHasHovered] = useState(false);

    // Memoize the onMouseEnter handler
    const handleMouseEnter = useCallback(
      (event: React.MouseEvent<HTMLElement>) => {
        setHasHovered(true);
        if (prefetchConfig.onMouseEnter) {
          prefetchConfig.onMouseEnter(event); // Call original onMouseEnter if provided
        }
      },
      [prefetchConfig],
    );

    useCheckPermission({
      resource: prefetchConfig.prefetch?.permission?.resource ?? "workspace",
      action: prefetchConfig.prefetch?.permission?.action ?? "view",
      resourceId: prefetchConfig.prefetch?.permission?.resourceId ?? "",
      enabled: prefetchConfig.prefetch?.permission && hasHovered,
    });

    useCheckPermissionList({
      permissions: prefetchConfig.prefetch?.permissionList ?? [],
      enabled: !!prefetchConfig.prefetch?.permissionList && hasHovered,
    });

    return typeof children === "function"
      ? children({ onMouseEnter: handleMouseEnter })
      : React.cloneElement(children, {
          onMouseEnter: (event: React.MouseEvent<HTMLElement>) => {
            handleMouseEnter(event);
            // If the child already has an onMouseEnter prop, call it too
            if (children.props.onMouseEnter) {
              children.props.onMouseEnter(event);
            }
          },
          ref,
        });
  },
);

PrefetchHandler.displayName = "PrefetchHandler";
